import * as moment from 'moment';
import * as R from 'ramda';

const FIRST_DAY = 1;
const LAST_DAY = 31;
const FIRST_MONTH = 1;
const LAST_MONTH = 11;
export const HOUR_IN_MILLIS = 3600000;

export const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm';


export const getCurrentMillis = () => moment().valueOf();

export const getMillis = (date: string | number | Date) => moment(date).valueOf();

export const getMoment = (date: number | string | Date): moment.Moment => moment(date);

export const getMomentWithFormat = (date: number | string | Date, format: string): moment.Moment => moment(date, format);

export const getYear = (date: string | number | Date) => moment(date).year();

export const getMinutes = (date: string | number | Date) => moment(date).minutes();

export const getHours = (date: string | number | Date) => moment(date).hours();

export const calculateDayDifferenceBetweenMillis = (from: number | string | Date, to: number | string | Date) => {
  const fromTime = getMoment(from).startOf('day');
  const toTime = getMoment(to).startOf('day');

  return fromTime.diff(toTime, 'days');
};

export const calculateMinutesDifferenceBetweenMillis = (to: number, from: number) => {
  return ((to - from) / (1000 * 60));
};

export const formatMillis = R.curry((format: string, millis: number): string => getMoment(millis).format(format));

export const getFirstMillisOfDateWithCorrection = (date: string | number | Date, hours: number) => (
  moment(date).startOf('date').subtract(hours, 'hour').valueOf()
);

export const getLastMillisOfDateWithCorrection = (date: string | number | Date, hours: number) => (
  moment(date).endOf('date').add(hours, 'hour').valueOf()
);

export const getFirstMillisOfYearMonth = (year: number, month: number) => (
  moment({ y: year, M: month, d: FIRST_DAY }).startOf('month').valueOf()
);

export const getLastMillisOfYearMonth = (year: number, month: number) => (
  moment({ y: year, M: month, d: FIRST_DAY }).endOf('month').valueOf()
);

export const getFirstMillisOfYear = (year: number) => (
  moment({ y: year, M: FIRST_MONTH, d: FIRST_DAY }).startOf('year').valueOf()
);

export const getLastMillisOfYear = (year: number) => (
  moment({ y: year, M: LAST_MONTH, d: LAST_DAY }).endOf('month').valueOf()
);

export const getFirstMillisOfDate = (date: string | number | Date) => (
  getFirstMillisOfDateWithCorrection(date, 0)
);

export const getLastMillisOfDate = (date: string | number | Date) => (
  getLastMillisOfDateWithCorrection(date, 0)
);

export const getMillisFromStringWithFormat = R.curry((format: string, date: string) => moment(date, format).valueOf());

export const subtractDays = R.curry((days: number, date: string | number | Date) => moment(date).subtract(days, 'days').valueOf());

export const subtractHour = R.curry((hours: number, date: string | number | Date) => moment(date).subtract(hours, 'hour').valueOf());

export const addDays = R.curry((days: number, date: string | number | Date) => moment(date).add(days, 'days').valueOf());

export const addYears = R.curry((years: number, date: string | number | Date) => moment(date).add(years, 'years').valueOf());

export const addMinutes = R.curry((minutes: number, date: string | number | Date) => moment(date).add(minutes, 'minutes').valueOf());

export const addMillis = R.curry((millis: number, date: string | number | Date) => moment(date).add(millis, 'milliseconds').valueOf());

export const toDate = (date: string | number | Date) => moment(date).toDate();

export const getDatesBetweenDates = (start: string | number | Date, end: string | number | Date) => {
  const dates = [];
  for (let date = start; date <= end; date = addDays(1, date)) {
    dates.push(getFirstMillisOfDate(date));
  }

  return dates;
};

export const dateBetweenDates = (date: string | number | Date, from: string | number | Date, to: string | number | Date) => {
  if (date >= from && date <= to) {
    return true;
  }

  return false;
};

export const isSameDay = (date: string | number | Date, otherDate: string | number | Date) => getMoment(date).isSame(otherDate, 'days');

export const getWeekendDaysBetweenDates = (start: string | number | Date, end: string | number | Date) => {
  let dates = [];
  for (let date = start; date <= end; date = addDays(1, date)) {
    if (moment(date).day() === 0 || moment(date).day() === 6) {
      dates = [...dates, getFirstMillisOfDate(date)];
    }
  }
  return dates;
};

export const getCurrentDate = () => moment().format('YYYY-MM-DD');

export const getDateAndHour = (date: string | number | Date) => moment(date).format('DD/MM/YYYY HH:mm');

export const getDate = (date: string | number | Date) => moment(date).format('DD/MM/YYYY');

export const getTimeFromDate = (date: string | number | Date) => moment(date).seconds(0).format('HH:mm:ss');

export const getTimeFromDateFormatted = (date: string | number | Date, format: string) => moment(date).seconds(0).format(format);

export const getStartOfDay = (date: string | number | Date) => getMoment(date).startOf('day').valueOf();

export const getEndOfDay = (date: string | number | Date) => getMoment(date).endOf('day').valueOf();

// Fix para la linea de "ahora" cuando el cambio de hora (verano/invierno) cae dentro de la ventana visible.
// La rejilla de horas asume dias de 24 h, asi que se compensa la diferencia real de offset UTC
// entre el inicio de la ventana y el instante a corregir (ej. +60 min en octubre, -60 min en marzo).
export const fixHourByTimezoneChangeBetweenDates =
  (hourToFix: number, startDate: string | number | Date, _endDate?: string | number | Date) => {
    const offsetDifferenceMinutes = getMoment(hourToFix).utcOffset() - getMoment(startDate).utcOffset();

    return hourToFix + offsetDifferenceMinutes * 60000;
  };



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/utils/date.utils.ts